import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MessageSquare, Clock, TrendingUp, Plus, ChevronRight, ChevronLeft,
  Briefcase, Zap, Brain, Users, BarChart3, Target,
  Sparkles, ArrowRight, X, CreditCard, Crown, Check,
  Star, Mic, History, ShoppingBag, ArrowUpRight, ArrowDownRight, Minus,
  FileText, Upload, Loader2, Volume2, BookOpen, Send, CheckCircle, AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { loginUrlWithRedirect } from '../utils/urlConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERVIEW_TYPES = [
  { id: 'mixed', label: 'Mixed', desc: 'Technical + HR blend', icon: Sparkles },
  { id: 'technical', label: 'Technical', desc: 'DSA, system design, coding', icon: Brain },
  { id: 'hr', label: 'HR / Behavioral', desc: 'Soft skills & situational', icon: Users },
];

const MockInterviewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [plans, setPlans] = useState([]);
  const [purchasing, setPurchasing] = useState(null);
  const [history, setHistory] = useState({ sessions: [], total: 0, page: 1, total_pages: 1 });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  // Prep Coach state
  const [coachPlans, setCoachPlans] = useState([]);
  const [coachLoading, setCoachLoading] = useState(false);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [activePlan, setActivePlan] = useState(null);
  const [drillQuestion, setDrillQuestion] = useState(null);
  const [drillAnswer, setDrillAnswer] = useState('');
  const [drillSubmitting, setDrillSubmitting] = useState(false);
  const [drillFeedback, setDrillFeedback] = useState(null);
  const [form, setForm] = useState({
    job_description: '',
    job_title: '',
    company_name: '',
    interview_type: 'mixed',
    duration_minutes: 10,
    resume_text: '',
    mode: 'text',
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [uploadingResume, setUploadingResume] = useState(false);
  const resumeInputRef = React.useRef(null);

  const token = localStorage.getItem('codearena_token');
  const student = (() => { try { return JSON.parse(localStorage.getItem('codearena_student') || '{}'); } catch { return {}; } })();

  // Pre-fill from job listing navigation state
  useEffect(() => {
    if (location.state?.job_title || location.state?.job_description) {
      setForm(f => ({
        ...f,
        job_title: location.state.job_title || f.job_title,
        company_name: location.state.company_name || f.company_name,
        job_description: location.state.job_description || f.job_description,
      }));
      setShowNewModal(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (!token) { navigate(loginUrlWithRedirect('/codearena/auth'), { replace: true }); return; }
    fetchDashboard();
    fetchPlans();
  }, [token, navigate]);

  useEffect(() => {
    if (activeTab === 'history') fetchHistory(1);
    if (activeTab === 'analytics') fetchAnalytics();
    if (activeTab === 'coach') fetchCoachPlans();
  }, [activeTab]);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/mock-interview/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate(loginUrlWithRedirect('/codearena/auth'), { replace: true });
      else toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API}/mock-interview/plans`);
      setPlans(res.data.plans || []);
    } catch { /* silent */ }
  };

  const fetchHistory = async (page) => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/mock-interview/history?page=${page}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data);
    } catch { toast.error('Failed to load history'); }
    finally { setHistoryLoading(false); }
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/mock-interview/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(res.data);
    } catch { toast.error('Failed to load analytics'); }
    finally { setAnalyticsLoading(false); }
  };

  const purchasePlan = async (planId) => {
    setPurchasing(planId);
    try {
      const res = await axios.post(`${API}/mock-interview/purchase-plan`, {
        plan_id: planId,
        customer_name: student.full_name || 'Student',
        customer_email: student.email || '',
        customer_phone: student.phone || '9999999999'
      }, { headers: { Authorization: `Bearer ${token}` } });

      const { payment_session_id, order_id } = res.data;

      try {
        const { load } = await import('@cashfreepayments/cashfree-js');
        const cashfree = await load({ mode: 'production' });
        await cashfree.checkout({
          paymentSessionId: payment_session_id,
          redirectTarget: '_self'
        });
      } catch (sdkErr) {
        console.error('Cashfree SDK error:', sdkErr);
        toast.error('Payment gateway failed to load. Please try again.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setPurchasing(null);
    }
  };

  const startInterview = async () => {
    if (!form.job_description.trim()) {
      toast.error('Please enter a job description');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/mock-interview/sessions`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Interview started!');
      navigate(`/codearena/mock-interview/${res.data.session.id}`);
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('No credits remaining. Purchase a plan first!');
        setShowNewModal(false);
        setActiveTab('plans');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to start interview');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Max 10MB.');
      return;
    }
    setResumeFile(file);
    setUploadingResume(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/mock-interview/upload-resume`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setForm(f => ({ ...f, resume_text: res.data.resume_text }));
      toast.success(`Resume uploaded! Extracted ${res.data.text_length} characters.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process resume');
      setResumeFile(null);
    } finally {
      setUploadingResume(false);
    }
  };

  // Prep Coach functions
  const fetchCoachPlans = async () => {
    setCoachLoading(true);
    try {
      const res = await axios.get(`${API}/mock-interview/prep-coach/plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoachPlans(res.data.plans || []);
    } catch { /* silent */ }
    finally { setCoachLoading(false); }
  };

  const generateStudyPlan = async () => {
    setGeneratingPlan(true);
    try {
      const res = await axios.post(`${API}/mock-interview/prep-coach/generate`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      });
      setCoachPlans(prev => [res.data.plan, ...prev]);
      setActivePlan(res.data.plan);
      if (dashboard) setDashboard(prev => ({ ...prev, credits_remaining: res.data.credits_remaining }));
      toast.success('Study plan generated!');
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('No credits remaining. Purchase a plan first!');
        setActiveTab('plans');
      } else if (err.response?.status === 400) {
        toast.error(err.response?.data?.detail || 'Complete at least one interview first.');
      } else {
        toast.error('Failed to generate study plan. Try again.');
      }
    } finally {
      setGeneratingPlan(false);
    }
  };

  const startDrill = (question, topic) => {
    setDrillQuestion({ question: question.question, difficulty: question.difficulty, tip: question.tip, topic });
    setDrillAnswer('');
    setDrillFeedback(null);
  };

  const submitDrillAnswer = async () => {
    if (!drillAnswer.trim() || !drillQuestion) return;
    setDrillSubmitting(true);
    try {
      const res = await axios.post(`${API}/mock-interview/prep-coach/quick-drill`, {
        question: drillQuestion.question,
        answer: drillAnswer,
        topic: drillQuestion.topic
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 });
      setDrillFeedback(res.data.feedback);
    } catch {
      toast.error('Failed to evaluate answer.');
    } finally {
      setDrillSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B0714' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
          <span className="text-white/60 font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>Loading...</span>
        </div>
      </div>
    );
  }

  const d = dashboard || {};
  const credits = d.credits_remaining || 0;

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(ellipse at top center, #2A1B44 0%, #0B0714 50%, #0B0714 100%)', fontFamily: 'Outfit, sans-serif' }}>
      {/* Sidebar */}
      <div className="fixed left-0 top-0 bottom-0 w-64 z-30 hidden lg:flex flex-col border-r"
        style={{ background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(40px)', borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="p-6 border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <h1 className="text-xl font-black text-white tracking-tight">EvalentX</h1>
          <p className="text-xs text-emerald-400 font-semibold tracking-widest uppercase mt-1">Mock Interview</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {[
            { id: 'dashboard', icon: BarChart3, label: 'Dashboard' },
            { id: 'plans', icon: Crown, label: 'Plans & Credits' },
            { id: 'history', icon: History, label: 'Interview History' },
            { id: 'analytics', icon: TrendingUp, label: 'Analytics' },
            { id: 'coach', icon: BookOpen, label: 'Prep Coach' },
          ].map((item) => (
            <button key={item.id} onClick={() => setActiveTab(item.id)}
              data-testid={`sidebar-${item.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                activeTab === item.id ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white/80 hover:bg-white/5'
              }`}>
              <item.icon size={18} />
              {item.label}
            </button>
          ))}
        </nav>

        {/* Credits in sidebar */}
        <div className="p-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <div className="px-4 py-3 rounded-xl" style={{ background: 'rgba(0,229,155,0.1)' }}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold text-white/60 uppercase tracking-wider">Credits</span>
              <span className="text-lg font-black" style={{ color: '#00E59B' }} data-testid="sidebar-credits">{credits}</span>
            </div>
            <p className="text-[10px] text-white/40">interviews remaining</p>
            {credits === 0 && (
              <button onClick={() => setActiveTab('plans')} data-testid="buy-credits-sidebar"
                className="w-full mt-2 py-1.5 rounded-lg text-xs font-bold transition-all"
                style={{ background: '#00E59B', color: '#09090B' }}>
                Buy Credits
              </button>
            )}
          </div>
          <button onClick={() => navigate('/codearena/dashboard')} data-testid="back-to-arena"
            className="w-full flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-white/50 hover:text-white hover:bg-white/5 transition-all mt-2">
            <ArrowRight size={18} className="rotate-180" /> Back to Arena
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:ml-64 min-h-screen">
        {/* Mobile header */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <div>
            <h1 className="text-lg font-black text-white">Mock Interview</h1>
            <p className="text-xs text-emerald-400 font-semibold">EvalentX Arena</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: 'rgba(0,229,155,0.15)' }}>
              <CreditCard size={14} style={{ color: '#00E59B' }} />
              <span className="text-sm font-bold" style={{ color: '#00E59B' }} data-testid="mobile-credits">{credits}</span>
            </div>
            <button onClick={() => navigate('/codearena/dashboard')} className="text-white/50 hover:text-white text-sm font-medium">Back</button>
          </div>
        </div>

        {/* Mobile tab bar */}
        <div className="lg:hidden flex border-b overflow-x-auto" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          {[
            { id: 'dashboard', label: 'Dashboard' },
            { id: 'plans', label: 'Plans' },
            { id: 'history', label: 'History' },
            { id: 'analytics', label: 'Analytics' },
            { id: 'coach', label: 'Prep Coach' },
          ].map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-semibold whitespace-nowrap transition-all border-b-2 ${
                activeTab === tab.id ? 'text-white border-emerald-400' : 'text-white/40 border-transparent'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">

          {/* ============ DASHBOARD TAB ============ */}
          {activeTab === 'dashboard' && (
            <div className="space-y-8">
              {/* Header */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" data-testid="dashboard-title">
                    Interview Dashboard
                  </h2>
                  <p className="text-white/40 mt-1 text-sm">Practice makes perfect. Track your progress.</p>
                </div>
                <button onClick={() => credits > 0 ? setShowNewModal(true) : setActiveTab('plans')} data-testid="start-new-interview-btn"
                  className="flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-all hover:-translate-y-0.5"
                  style={{ background: '#00E59B', color: '#09090B', boxShadow: '0 0 20px rgba(0,229,155,0.3)' }}>
                  <Plus size={18} /> {credits > 0 ? 'Start New Interview' : 'Buy Credits to Start'}
                </button>
              </div>

              {/* Credits Banner (if low or zero) */}
              {credits <= 2 && (
                <div className={`rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
                  credits === 0 ? 'bg-red-500/10 border border-red-500/20' : 'bg-amber-500/10 border border-amber-500/20'
                }`}>
                  <div className="flex items-center gap-3">
                    <CreditCard size={24} className={credits === 0 ? 'text-red-400' : 'text-amber-400'} />
                    <div>
                      <p className="text-white font-semibold text-sm">
                        {credits === 0 ? 'No interview credits remaining' : `Only ${credits} credit${credits > 1 ? 's' : ''} left`}
                      </p>
                      <p className="text-white/50 text-xs">
                        {credits === 0 ? 'Purchase a plan to start practicing' : 'Top up before they run out'}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setActiveTab('plans')} data-testid="buy-credits-banner"
                    className="shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-all hover:-translate-y-0.5"
                    style={{ background: '#00E59B', color: '#09090B' }}>
                    <Crown size={14} /> View Plans
                  </button>
                </div>
              )}

              {/* Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
                {[
                  { label: 'Credits Left', value: credits, icon: CreditCard, color: '#00E59B' },
                  { label: 'Completed', value: d.completed_sessions || 0, icon: Target, color: '#00E59B' },
                  { label: 'Avg. Score', value: `${d.average_score || 0}%`, icon: TrendingUp, color: '#00E59B' },
                  { label: 'Communication', value: `${d.average_communication || 0}%`, icon: Mic, color: '#00E59B' },
                ].map((stat, i) => (
                  <div key={i} data-testid={`stat-${stat.label.toLowerCase().replace(/[\s.]/g, '-')}`}
                    className="bg-white rounded-2xl p-5 sm:p-6 transition-all duration-300 hover:-translate-y-1"
                    style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[10px] sm:text-xs font-bold text-zinc-400 uppercase tracking-wider">{stat.label}</span>
                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}20` }}>
                        <stat.icon size={14} style={{ color: stat.color }} />
                      </div>
                    </div>
                    <span className="text-2xl sm:text-3xl font-black text-zinc-900">{stat.value}</span>
                  </div>
                ))}
              </div>

              {/* Score & Recent */}
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div className="lg:col-span-2 bg-white rounded-2xl p-6" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                  <h3 className="text-lg font-bold text-zinc-900 mb-6">Performance Overview</h3>
                  <div className="grid grid-cols-2 gap-6">
                    {[
                      { label: 'Overall', score: d.average_score || 0 },
                      { label: 'Technical', score: d.average_technical || 0 },
                      { label: 'Comm.', score: d.average_communication || 0 },
                      { label: 'Confidence', score: d.average_confidence || 0 },
                    ].map((item) => {
                      const r = 34, circ = 2 * Math.PI * r, off = circ - (item.score / 100) * circ;
                      return (
                        <div key={item.label} className="flex flex-col items-center gap-1" data-testid={`score-${item.label.toLowerCase()}`}>
                          <svg width={80} height={80} className="transform -rotate-90">
                            <circle cx={40} cy={40} r={r} fill="none" stroke="#E4E4E7" strokeWidth={6} />
                            <circle cx={40} cy={40} r={r} fill="none" stroke="#00E59B" strokeWidth={6}
                              strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={off}
                              className="transition-all duration-1000" />
                          </svg>
                          <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider -mt-12 mb-6">{item.score}</span>
                          <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">{item.label}</span>
                        </div>
                      );
                    })}
                  </div>
                  {d.completed_sessions === 0 && (
                    <p className="text-center text-zinc-400 text-sm mt-4">Complete your first interview to see scores</p>
                  )}
                </div>

                <div className="lg:col-span-3 bg-white rounded-2xl p-6" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-zinc-900">Recent Interviews</h3>
                    {d.recent_sessions?.length > 0 && (
                      <button onClick={() => setActiveTab('history')} className="text-xs font-bold" style={{ color: '#00E59B' }}>
                        View All
                      </button>
                    )}
                  </div>
                  {(!d.recent_sessions || d.recent_sessions.length === 0) ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: '#00E59B20' }}>
                        <Sparkles size={28} style={{ color: '#00E59B' }} />
                      </div>
                      <p className="text-zinc-900 font-semibold mb-1">No interviews yet</p>
                      <p className="text-zinc-400 text-sm mb-4">Purchase a plan and start practicing</p>
                      <button onClick={() => setActiveTab('plans')} data-testid="empty-buy-plan"
                        className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all hover:-translate-y-0.5"
                        style={{ background: '#00E59B', color: '#09090B' }}>
                        <Crown size={16} /> Get Started
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {d.recent_sessions.map((session) => (
                        <button key={session.id} data-testid={`session-${session.id}`}
                          onClick={() => navigate(`/codearena/mock-interview/${session.id}`)}
                          className="w-full flex items-center gap-4 p-4 rounded-xl border border-zinc-100 hover:border-zinc-200 hover:bg-zinc-50 transition-all text-left group">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                            session.status === 'completed' ? 'bg-emerald-50' : 'bg-amber-50'
                          }`}>
                            {session.status === 'completed'
                              ? <Target size={18} className="text-emerald-600" />
                              : <Clock size={18} className="text-amber-600" />
                            }
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-zinc-900 truncate">{session.job_title}</p>
                            <p className="text-xs text-zinc-400 mt-0.5">
                              {((session.interview_type || 'mixed').charAt(0).toUpperCase() + (session.interview_type || 'mixed').slice(1))} &middot; {session.created_at ? new Date(session.created_at).toLocaleDateString() : '—'}
                            </p>
                          </div>
                          {session.status === 'completed' && session.feedback && (
                            <span className="text-sm font-bold" style={{ color: '#00E59B' }}>{session.feedback.overall_score}%</span>
                          )}
                          {session.status === 'active' && (
                            <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full">IN PROGRESS</span>
                          )}
                          <ChevronRight size={16} className="text-zinc-300 group-hover:text-zinc-500 transition-colors" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Start Cards */}
              <div>
                <h3 className="text-lg font-bold text-white mb-4">Quick Start</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {INTERVIEW_TYPES.map((type) => (
                    <button key={type.id} data-testid={`quick-start-${type.id}`}
                      onClick={() => {
                        if (credits > 0) {
                          setForm(f => ({ ...f, interview_type: type.id }));
                          setShowNewModal(true);
                        } else {
                          setActiveTab('plans');
                        }
                      }}
                      className="bg-white rounded-2xl p-6 text-left transition-all duration-300 hover:-translate-y-1 group"
                      style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: '#00E59B20' }}>
                        <type.icon size={20} style={{ color: '#00E59B' }} />
                      </div>
                      <h4 className="font-bold text-zinc-900">{type.label}</h4>
                      <p className="text-sm text-zinc-400 mt-1">{type.desc}</p>
                      <div className="flex items-center gap-1 mt-3 text-xs font-bold" style={{ color: '#00E59B' }}>
                        {credits > 0 ? 'Start' : 'Buy Plan'} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ============ PLANS TAB ============ */}
          {activeTab === 'plans' && (
            <div className="space-y-8">
              <div className="text-center max-w-xl mx-auto">
                <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" data-testid="plans-title">
                  Choose Your Plan
                </h2>
                <p className="text-white/40 mt-2 text-sm">
                  Get AI-powered mock interviews with detailed scoring and feedback
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
                {plans.map((plan) => (
                  <div key={plan.id} data-testid={`plan-${plan.id}`}
                    className={`bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 relative ${
                      plan.popular ? 'ring-2' : ''
                    }`}
                    style={{
                      boxShadow: plan.popular ? '0 8px 40px rgba(0,229,155,0.3)' : '0 8px 32px rgba(0,0,0,0.4)',
                      ringColor: plan.popular ? '#00E59B' : 'transparent'
                    }}>
                    {plan.popular && (
                      <div className="text-center py-1.5 text-xs font-bold uppercase tracking-wider" style={{ background: '#00E59B', color: '#09090B' }}>
                        Most Popular
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-lg font-bold text-zinc-900">{plan.name}</h3>
                      <p className="text-sm text-zinc-400 mt-1">{plan.description}</p>
                      <div className="mt-4 flex items-baseline gap-1">
                        <span className="text-4xl font-black text-zinc-900">&#8377;{plan.price}</span>
                      </div>
                      <div className="mt-2 text-sm font-semibold" style={{ color: '#00E59B' }}>
                        {plan.interviews} interview{plan.interviews > 1 ? 's' : ''} &middot; {plan.duration_minutes} min each
                      </div>
                      <ul className="mt-5 space-y-2.5">
                        {plan.features.map((f, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-zinc-600">
                            <Check size={15} className="text-emerald-500 shrink-0" />
                            {f}
                          </li>
                        ))}
                      </ul>
                      <button onClick={() => purchasePlan(plan.id)} disabled={purchasing === plan.id}
                        data-testid={`buy-plan-${plan.id}`}
                        className={`w-full mt-6 py-3 rounded-full font-bold text-sm transition-all hover:-translate-y-0.5 disabled:opacity-50 ${
                          plan.popular ? '' : 'border-2 border-zinc-200 text-zinc-900 hover:border-zinc-300'
                        }`}
                        style={plan.popular ? { background: '#00E59B', color: '#09090B', boxShadow: '0 0 20px rgba(0,229,155,0.3)' } : {}}>
                        {purchasing === plan.id ? (
                          <span className="flex items-center justify-center gap-2">
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            Processing...
                          </span>
                        ) : (
                          `Buy ${plan.name}`
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Current Credits */}
              <div className="max-w-md mx-auto bg-white/5 border rounded-2xl p-5 text-center" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                <p className="text-white/60 text-sm">
                  You currently have <span className="font-bold text-white">{credits}</span> interview credit{credits !== 1 ? 's' : ''} remaining
                </p>
              </div>
            </div>
          )}

          {/* ============ HISTORY TAB ============ */}
          {activeTab === 'history' && (
            <div className="space-y-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" data-testid="history-title">
                Interview History
              </h2>

              {historyLoading ? (
                <div className="flex justify-center py-16">
                  <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : history.sessions.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                  <History size={40} className="text-zinc-300 mx-auto mb-4" />
                  <p className="text-zinc-900 font-semibold mb-1">No interviews yet</p>
                  <p className="text-zinc-400 text-sm">Your completed interviews will appear here</p>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {history.sessions.map((session) => (
                      <button key={session.id} data-testid={`history-session-${session.id}`}
                        onClick={() => navigate(`/codearena/mock-interview/${session.id}`)}
                        className="w-full bg-white rounded-2xl p-5 flex items-center gap-4 text-left transition-all hover:-translate-y-0.5 group"
                        style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                          session.status === 'completed' ? 'bg-emerald-50' : 'bg-amber-50'
                        }`}>
                          {session.status === 'completed'
                            ? <Target size={22} className="text-emerald-600" />
                            : <Clock size={22} className="text-amber-600" />
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-zinc-900 truncate">{session.job_title}</p>
                          <div className="flex items-center gap-3 text-xs text-zinc-400 mt-1">
                            <span className="capitalize">{session.interview_type}</span>
                            <span>&middot;</span>
                            <span>{session.duration_minutes} min</span>
                            <span>&middot;</span>
                            <span>{new Date(session.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        {session.status === 'completed' && session.feedback && (
                          <div className="text-right shrink-0">
                            <span className="text-xl font-black" style={{ color: session.feedback.overall_score >= 70 ? '#00E59B' : session.feedback.overall_score >= 40 ? '#F5A623' : '#FF3B30' }}>
                              {session.feedback.overall_score}%
                            </span>
                            <p className="text-[10px] text-zinc-400 font-semibold uppercase">Score</p>
                          </div>
                        )}
                        {session.status === 'active' && (
                          <span className="text-xs font-bold text-amber-600 bg-amber-50 px-3 py-1.5 rounded-full shrink-0">ACTIVE</span>
                        )}
                        <ChevronRight size={18} className="text-zinc-300 group-hover:text-zinc-500 transition-colors shrink-0" />
                      </button>
                    ))}
                  </div>

                  {/* Pagination */}
                  {history.total_pages > 1 && (
                    <div className="flex items-center justify-center gap-3 mt-6">
                      <button onClick={() => fetchHistory(history.page - 1)} disabled={history.page <= 1}
                        data-testid="prev-page"
                        className="w-9 h-9 rounded-full flex items-center justify-center border transition-all hover:bg-white/10 disabled:opacity-30"
                        style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                        <ChevronLeft size={16} className="text-white" />
                      </button>
                      <span className="text-white/60 text-sm font-medium">
                        Page {history.page} of {history.total_pages}
                      </span>
                      <button onClick={() => fetchHistory(history.page + 1)} disabled={history.page >= history.total_pages}
                        data-testid="next-page"
                        className="w-9 h-9 rounded-full flex items-center justify-center border transition-all hover:bg-white/10 disabled:opacity-30"
                        style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                        <ChevronRight size={16} className="text-white" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ============ ANALYTICS TAB ============ */}
          {activeTab === 'analytics' && (
            <div className="space-y-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" data-testid="analytics-title">
                Performance Analytics
              </h2>

              {analyticsLoading ? (
                <div className="flex justify-center py-16">
                  <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : !analytics || analytics.total_completed === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                  <TrendingUp size={40} className="text-zinc-300 mx-auto mb-4" />
                  <p className="text-zinc-900 font-semibold mb-1">Not enough data yet</p>
                  <p className="text-zinc-400 text-sm">Complete at least one interview to see analytics</p>
                </div>
              ) : (
                <>
                  {/* Improvement Banner */}
                  {analytics.improvement && Object.keys(analytics.improvement).length > 0 && (
                    <div className="bg-white rounded-2xl p-6" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                      <h3 className="text-lg font-bold text-zinc-900 mb-4">Progress Since First Interview</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {[
                          { key: 'overall', label: 'Overall' },
                          { key: 'communication', label: 'Communication' },
                          { key: 'technical_depth', label: 'Technical' },
                          { key: 'confidence', label: 'Confidence' },
                        ].map((item) => {
                          const val = analytics.improvement[item.key] || 0;
                          return (
                            <div key={item.key} className="text-center p-3 rounded-xl bg-zinc-50" data-testid={`improvement-${item.key}`}>
                              <div className="flex items-center justify-center gap-1 mb-1">
                                {val > 0 ? <ArrowUpRight size={18} className="text-emerald-500" /> :
                                 val < 0 ? <ArrowDownRight size={18} className="text-red-500" /> :
                                 <Minus size={18} className="text-zinc-400" />}
                                <span className={`text-2xl font-black ${
                                  val > 0 ? 'text-emerald-600' : val < 0 ? 'text-red-600' : 'text-zinc-400'
                                }`}>
                                  {val > 0 ? '+' : ''}{val}
                                </span>
                              </div>
                              <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">{item.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Score Trend Chart */}
                  {analytics.trend_data.length > 1 && (
                    <div className="bg-white rounded-2xl p-6" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                      <h3 className="text-lg font-bold text-zinc-900 mb-4">Score Trend</h3>
                      <div className="overflow-x-auto">
                        <svg viewBox={`0 0 ${Math.max(analytics.trend_data.length * 80, 400)} 220`} className="w-full" style={{ minWidth: '400px' }}>
                          {[0, 25, 50, 75, 100].map((v) => (
                            <g key={v}>
                              <line x1={40} y1={200 - v * 1.8} x2={Math.max(analytics.trend_data.length * 80, 400) - 20} y2={200 - v * 1.8}
                                stroke="#f4f4f5" strokeWidth={1} />
                              <text x={10} y={205 - v * 1.8} className="text-[10px]" fill="#a1a1aa">{v}</text>
                            </g>
                          ))}
                          {[
                            { key: 'overall', color: '#00E59B' },
                            { key: 'communication', color: '#3B82F6' },
                            { key: 'technical_depth', color: '#F59E0B' },
                            { key: 'confidence', color: '#A855F7' },
                          ].map((line) => {
                            const points = analytics.trend_data.map((d, i) => ({
                              x: 60 + i * 70, y: 200 - (d[line.key] || 0) * 1.8
                            }));
                            const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
                            return (
                              <g key={line.key}>
                                <path d={pathD} fill="none" stroke={line.color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" opacity={0.8} />
                                {points.map((p, i) => (
                                  <circle key={i} cx={p.x} cy={p.y} r={3.5} fill={line.color} stroke="white" strokeWidth={2} />
                                ))}
                              </g>
                            );
                          })}
                          {analytics.trend_data.map((d, i) => (
                            <text key={i} x={60 + i * 70} y={218} textAnchor="middle" className="text-[9px]" fill="#a1a1aa">#{i + 1}</text>
                          ))}
                        </svg>
                      </div>
                      <div className="flex flex-wrap items-center justify-center gap-4 mt-4">
                        {[
                          { label: 'Overall', color: '#00E59B' },
                          { label: 'Communication', color: '#3B82F6' },
                          { label: 'Technical', color: '#F59E0B' },
                          { label: 'Confidence', color: '#A855F7' },
                        ].map((item) => (
                          <div key={item.label} className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded-full" style={{ background: item.color }} />
                            <span className="text-xs font-semibold text-zinc-500">{item.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Bottom Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {analytics.type_distribution.length > 0 && (
                      <div className="bg-white rounded-2xl p-6" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                        <h3 className="text-base font-bold text-zinc-900 mb-4">Interview Types</h3>
                        <div className="space-y-3">
                          {analytics.type_distribution.map((td) => {
                            const pct = Math.round((td.count / analytics.total_completed) * 100);
                            return (
                              <div key={td.type} data-testid={`type-dist-${td.type}`}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-semibold text-zinc-700 capitalize">{td.type}</span>
                                  <span className="text-xs text-zinc-400">{td.count} ({pct}%) &middot; Avg: {td.avg_score}%</span>
                                </div>
                                <div className="w-full h-2 rounded-full bg-zinc-100 overflow-hidden">
                                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: '#00E59B' }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="bg-white rounded-2xl p-6" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                      <h3 className="text-base font-bold text-zinc-900 mb-4">Key Insights</h3>
                      <div className="space-y-4">
                        <div className="flex items-start gap-3 p-3 rounded-xl bg-emerald-50">
                          <ArrowUpRight size={20} className="text-emerald-600 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-bold text-emerald-800">Strongest Area</p>
                            <p className="text-xs text-emerald-600 capitalize mt-0.5" data-testid="best-area">
                              {analytics.best_area?.replace(/_/g, ' ') || 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50">
                          <Target size={20} className="text-amber-600 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-bold text-amber-800">Focus Area</p>
                            <p className="text-xs text-amber-600 capitalize mt-0.5" data-testid="worst-area">
                              {analytics.worst_area?.replace(/_/g, ' ') || 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 rounded-xl bg-zinc-50">
                          <BarChart3 size={20} className="text-zinc-500 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-bold text-zinc-700">Interviews Analyzed</p>
                            <p className="text-xs text-zinc-500 mt-0.5" data-testid="total-analyzed">
                              {analytics.total_completed} completed session{analytics.total_completed !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ============ PREP COACH TAB ============ */}
          {activeTab === 'coach' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" data-testid="coach-title">
                    Prep Coach
                  </h2>
                  <p className="text-white/40 mt-1 text-sm">AI-powered study plans targeting your weak areas</p>
                </div>
                <button onClick={generateStudyPlan} disabled={generatingPlan || credits <= 0}
                  data-testid="generate-plan-btn"
                  className="flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-all hover:-translate-y-0.5 disabled:opacity-50"
                  style={{ background: '#00E59B', color: '#09090B', boxShadow: '0 0 20px rgba(0,229,155,0.3)' }}>
                  {generatingPlan ? (
                    <><Loader2 size={16} className="animate-spin" /> Generating Plan...</>
                  ) : (
                    <><BookOpen size={16} /> Generate Study Plan (1 credit)</>
                  )}
                </button>
              </div>

              {coachLoading ? (
                <div className="flex justify-center py-16">
                  <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : activePlan ? (
                /* Active Plan Detail View */
                <div className="space-y-6">
                  <button onClick={() => { setActivePlan(null); setDrillQuestion(null); setDrillFeedback(null); }}
                    data-testid="back-to-plans"
                    className="flex items-center gap-2 text-white/50 hover:text-white text-sm font-semibold transition-colors">
                    <ChevronLeft size={16} /> All Plans
                  </button>

                  <div className="bg-white rounded-2xl p-6" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                    <h3 className="text-lg font-bold text-zinc-900" data-testid="plan-title">{activePlan.title}</h3>
                    <p className="text-sm text-zinc-500 mt-1">{activePlan.summary}</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {(activePlan.weak_areas_identified || []).map((area, i) => (
                        <span key={i} className="text-xs font-bold px-2.5 py-1 rounded-full bg-red-50 text-red-600" data-testid={`weak-area-${i}`}>
                          {area}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-zinc-400 mt-3">
                      Based on {activePlan.sessions_analyzed} interview{activePlan.sessions_analyzed !== 1 ? 's' : ''} &middot; Generated {new Date(activePlan.created_at).toLocaleDateString()}
                    </p>
                  </div>

                  {/* Topics */}
                  {(activePlan.topics || []).map((topic, ti) => (
                    <div key={ti} className="bg-white rounded-2xl p-6" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}
                      data-testid={`topic-${ti}`}>
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <h4 className="font-bold text-zinc-900">{topic.name}</h4>
                          <p className="text-sm text-zinc-500 mt-0.5">{topic.why}</p>
                        </div>
                        <span className="shrink-0 text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: '#00E59B20', color: '#059669' }}>
                          {topic.practice_questions?.length || 0} questions
                        </span>
                      </div>

                      {topic.key_concepts && (
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {topic.key_concepts.map((c, ci) => (
                            <span key={ci} className="text-xs font-medium px-2 py-0.5 rounded bg-zinc-100 text-zinc-600">{c}</span>
                          ))}
                        </div>
                      )}

                      <div className="space-y-3">
                        {(topic.practice_questions || []).map((q, qi) => (
                          <div key={qi} className="border border-zinc-100 rounded-xl p-4" data-testid={`question-${ti}-${qi}`}>
                            <div className="flex items-start justify-between gap-3">
                              <p className="text-sm font-semibold text-zinc-800 flex-1">{q.question}</p>
                              <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                                q.difficulty === 'easy' ? 'bg-emerald-50 text-emerald-600' :
                                q.difficulty === 'hard' ? 'bg-red-50 text-red-600' :
                                'bg-amber-50 text-amber-600'
                              }`}>{q.difficulty}</span>
                            </div>
                            {q.tip && <p className="text-xs text-zinc-400 mt-1.5 italic">Tip: {q.tip}</p>}
                            <button onClick={() => startDrill(q, topic.name)} data-testid={`drill-${ti}-${qi}`}
                              className="mt-3 flex items-center gap-1.5 text-xs font-bold transition-all hover:-translate-y-0.5"
                              style={{ color: '#00E59B' }}>
                              <Zap size={12} /> Practice This Question
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Quick Drill Section */}
                  {drillQuestion && (
                    <div className="bg-white rounded-2xl p-6 border-2" style={{ borderColor: '#00E59B', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}
                      data-testid="drill-section">
                      <h4 className="font-bold text-zinc-900 mb-1">Quick Drill</h4>
                      <p className="text-sm text-zinc-700 mb-4">{drillQuestion.question}</p>

                      {!drillFeedback ? (
                        <>
                          <textarea value={drillAnswer} onChange={e => setDrillAnswer(e.target.value)}
                            data-testid="drill-answer-input" rows={4}
                            placeholder="Type your answer here..."
                            className="w-full bg-zinc-50 border border-zinc-200 text-zinc-900 rounded-xl px-4 py-3 text-sm focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none transition-all resize-none mb-3" />
                          <div className="flex gap-3">
                            <button onClick={submitDrillAnswer} disabled={drillSubmitting || !drillAnswer.trim()}
                              data-testid="submit-drill-btn"
                              className="flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-all hover:-translate-y-0.5 disabled:opacity-50"
                              style={{ background: '#00E59B', color: '#09090B' }}>
                              {drillSubmitting ? <><Loader2 size={14} className="animate-spin" /> Evaluating...</> : <><Send size={14} /> Submit Answer</>}
                            </button>
                            <button onClick={() => { setDrillQuestion(null); setDrillFeedback(null); }}
                              className="px-5 py-2.5 rounded-full font-bold text-sm border border-zinc-200 text-zinc-600 hover:bg-zinc-50 transition-all">
                              Cancel
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="space-y-3" data-testid="drill-feedback">
                          <div className="flex items-center gap-3">
                            <span className={`text-2xl font-black ${
                              drillFeedback.score >= 70 ? 'text-emerald-600' : drillFeedback.score >= 40 ? 'text-amber-600' : 'text-red-600'
                            }`}>{drillFeedback.score}%</span>
                            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                              drillFeedback.rating === 'good' ? 'bg-emerald-50 text-emerald-700' :
                              drillFeedback.rating === 'average' ? 'bg-amber-50 text-amber-700' :
                              'bg-red-50 text-red-700'
                            }`}>
                              {drillFeedback.rating === 'good' ? 'Good' : drillFeedback.rating === 'average' ? 'Average' : 'Needs Work'}
                            </span>
                          </div>
                          <p className="text-sm text-zinc-700">{drillFeedback.feedback}</p>
                          {drillFeedback.improved_answer && (
                            <div className="bg-emerald-50 rounded-xl p-4">
                              <p className="text-xs font-bold text-emerald-800 mb-1">Model Answer:</p>
                              <p className="text-sm text-emerald-700">{drillFeedback.improved_answer}</p>
                            </div>
                          )}
                          <button onClick={() => { setDrillQuestion(null); setDrillFeedback(null); }}
                            data-testid="close-drill-btn"
                            className="flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-all hover:-translate-y-0.5"
                            style={{ background: '#00E59B', color: '#09090B' }}>
                            <CheckCircle size={14} /> Done
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : coachPlans.length === 0 ? (
                /* Empty State */
                <div className="bg-white rounded-2xl p-12 text-center" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: '#00E59B20' }}>
                    <BookOpen size={28} style={{ color: '#00E59B' }} />
                  </div>
                  <p className="text-zinc-900 font-semibold mb-1">No study plans yet</p>
                  <p className="text-zinc-400 text-sm mb-4">
                    Complete at least one interview, then generate a personalized study plan.
                  </p>
                  <button onClick={generateStudyPlan} disabled={generatingPlan || credits <= 0}
                    data-testid="generate-plan-empty-btn"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-all hover:-translate-y-0.5 disabled:opacity-50"
                    style={{ background: '#00E59B', color: '#09090B' }}>
                    {generatingPlan ? <><Loader2 size={14} className="animate-spin" /> Generating...</> : <><BookOpen size={16} /> Generate Study Plan (1 credit)</>}
                  </button>
                </div>
              ) : (
                /* Plans List */
                <div className="space-y-3">
                  {coachPlans.map((plan) => (
                    <button key={plan.id} onClick={() => setActivePlan(plan)} data-testid={`coach-plan-${plan.id}`}
                      className="w-full bg-white rounded-2xl p-5 flex items-center gap-4 text-left transition-all hover:-translate-y-0.5 group"
                      style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#00E59B20' }}>
                        <BookOpen size={22} style={{ color: '#00E59B' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-zinc-900 truncate">{plan.title}</p>
                        <p className="text-xs text-zinc-400 mt-0.5 truncate">{plan.summary}</p>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {(plan.weak_areas_identified || []).slice(0, 3).map((a, i) => (
                            <span key={i} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-500">{a}</span>
                          ))}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xs text-zinc-400">{new Date(plan.created_at).toLocaleDateString()}</p>
                        <p className="text-xs text-zinc-400 mt-0.5">{plan.topics?.length || 0} topics</p>
                      </div>
                      <ChevronRight size={18} className="text-zinc-300 group-hover:text-zinc-500 transition-colors shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* New Interview Modal */}
      {showNewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-interview-modal"
            style={{ boxShadow: '0 32px 64px rgba(0,0,0,0.5)' }}>
            <div className="flex items-center justify-between p-6 border-b border-zinc-100">
              <div>
                <h3 className="text-lg font-bold text-zinc-900">Start New Interview</h3>
                <p className="text-sm text-zinc-400 mt-0.5">
                  {credits > 0 ? `${credits} credit${credits > 1 ? 's' : ''} remaining` : 'No credits - purchase a plan first'}
                </p>
              </div>
              <button onClick={() => setShowNewModal(false)} data-testid="close-modal-btn"
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-zinc-100 transition-colors">
                <X size={18} className="text-zinc-400" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2 block">Job Title</label>
                <input value={form.job_title} onChange={e => setForm(f => ({ ...f, job_title: e.target.value }))}
                  data-testid="input-job-title" placeholder="e.g. Full Stack Developer"
                  className="w-full bg-zinc-50 border border-zinc-200 text-zinc-900 rounded-xl px-4 py-3 text-sm focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none transition-all" />
              </div>
              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2 block">Company Name</label>
                <input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))}
                  data-testid="input-company-name" placeholder="e.g. Google, Amazon"
                  className="w-full bg-zinc-50 border border-zinc-200 text-zinc-900 rounded-xl px-4 py-3 text-sm focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none transition-all" />
              </div>
              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2 block">Job Description *</label>
                <textarea value={form.job_description} onChange={e => setForm(f => ({ ...f, job_description: e.target.value }))}
                  data-testid="input-job-description" rows={6}
                  placeholder="Paste the full job description here..."
                  className="w-full bg-zinc-50 border border-zinc-200 text-zinc-900 rounded-xl px-4 py-3 text-sm focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none transition-all resize-none" />
              </div>

              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-3 block">Interview Type</label>
                <div className="grid grid-cols-3 gap-3">
                  {INTERVIEW_TYPES.map((type) => (
                    <button key={type.id} data-testid={`type-${type.id}`}
                      onClick={() => setForm(f => ({ ...f, interview_type: type.id }))}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${
                        form.interview_type === type.id
                          ? 'border-emerald-400 bg-emerald-50'
                          : 'border-zinc-100 hover:border-zinc-200'
                      }`}>
                      <type.icon size={20} className={form.interview_type === type.id ? 'text-emerald-600' : 'text-zinc-400'} />
                      <span className={`text-xs font-bold ${form.interview_type === type.id ? 'text-emerald-700' : 'text-zinc-600'}`}>
                        {type.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2 block">
                  Duration (max {d.max_duration_minutes || 10} min)
                </label>
                <div className="flex gap-3">
                  {[10, 15, 20].map((mins) => (
                    <button key={mins} data-testid={`duration-${mins}`}
                      onClick={() => setForm(f => ({ ...f, duration_minutes: mins }))}
                      disabled={mins > (d.max_duration_minutes || 10)}
                      className={`flex-1 py-2.5 rounded-xl border-2 text-sm font-bold transition-all ${
                        form.duration_minutes === mins
                          ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                          : mins > (d.max_duration_minutes || 10)
                            ? 'border-zinc-50 text-zinc-300 cursor-not-allowed bg-zinc-50'
                            : 'border-zinc-100 text-zinc-500 hover:border-zinc-200'
                      }`}>
                      {mins} min
                    </button>
                  ))}
                </div>
              </div>

              {/* Resume Upload */}
              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2 block">
                  Resume (Optional - for personalized questions)
                </label>
                <input type="file" accept=".pdf" ref={resumeInputRef} className="hidden"
                  onChange={handleResumeUpload} data-testid="resume-file-input" />
                {!resumeFile ? (
                  <button onClick={() => resumeInputRef.current?.click()} disabled={uploadingResume}
                    data-testid="upload-resume-btn"
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-zinc-200 text-sm font-semibold text-zinc-400 hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50/50 transition-all">
                    <Upload size={16} /> Upload PDF Resume
                  </button>
                ) : (
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                    <FileText size={18} className="text-emerald-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-emerald-800 truncate">{resumeFile.name}</p>
                      <p className="text-xs text-emerald-600">
                        {uploadingResume ? 'Processing...' : form.resume_text ? 'Text extracted' : 'Ready'}
                      </p>
                    </div>
                    {uploadingResume ? (
                      <Loader2 size={16} className="text-emerald-600 animate-spin shrink-0" />
                    ) : (
                      <button onClick={() => { setResumeFile(null); setForm(f => ({ ...f, resume_text: '' })); }}
                        data-testid="remove-resume-btn"
                        className="text-zinc-400 hover:text-red-500 transition-colors shrink-0">
                        <X size={16} />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Interview Mode */}
              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-3 block">Interview Mode</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: 'text', label: 'Text Chat', desc: 'Type your answers', icon: MessageSquare },
                    { id: 'voice', label: 'Voice', desc: 'Speak your answers', icon: Mic },
                  ].map((mode) => (
                    <button key={mode.id} data-testid={`mode-${mode.id}`}
                      onClick={() => setForm(f => ({ ...f, mode: mode.id }))}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${
                        form.mode === mode.id
                          ? 'border-emerald-400 bg-emerald-50'
                          : 'border-zinc-100 hover:border-zinc-200'
                      }`}>
                      <mode.icon size={20} className={form.mode === mode.id ? 'text-emerald-600' : 'text-zinc-400'} />
                      <span className={`text-xs font-bold ${form.mode === mode.id ? 'text-emerald-700' : 'text-zinc-600'}`}>
                        {mode.label}
                      </span>
                      <span className="text-[10px] text-zinc-400">{mode.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-zinc-100">
              <button onClick={startInterview} disabled={creating || credits <= 0} data-testid="begin-interview-btn"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full font-bold text-sm transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: '#00E59B', color: '#09090B', boxShadow: '0 0 20px rgba(0,229,155,0.3)' }}>
                {creating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-zinc-800 border-t-transparent rounded-full animate-spin" />
                    Setting up interview...
                  </>
                ) : (
                  <>
                    <Zap size={18} /> Begin Interview (1 credit)
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MockInterviewPage;
